import { useEffect } from 'react';
import { useNavigate } from '@/providers/RouterShim';
import useBranch from '../../../../data/branch/useBranch';
import useCreateVehicle, {
  type CreateVehicleInputPayload,
  newVehicle,
} from '../../../../data/vehicles/useCreateVehicle';
import useAuth from '../../../../hooks/useAuth';
import { ShallowPanelBody } from '../../../../styled-components/components/Panel/ShallowPanel';
import { getMarketCode } from '../../../../utils/market';
import CreateRegisteredVehicleForm, {
  type VehicleFormSubmitData,
} from './CreateRegisteredVehicleForm';
import { CreateVehicleFooter } from './Footer';
import { type CreateVehiclePanels, separateRegVin } from './utils';

interface CreateRegisteredVehicleProps {
  onClose: () => void;
  navigatePanel: (panel: CreateVehiclePanels) => void;
  showTemplateSelect: boolean;
  setShowTemplateSelect: (showTemplateSelect: boolean) => void;
  disableVirtual?: boolean;
}

const CreateRegisteredVehicle = ({
  onClose,
  navigatePanel,
  showTemplateSelect,
  setShowTemplateSelect,
  disableVirtual,
}: CreateRegisteredVehicleProps) => {
  const navigate = useNavigate();
  const [branch] = useBranch();
  const { decodedToken } = useAuth();
  const [createVehicle, requestCreateVehicle, createVehicleResponse] = useCreateVehicle();
  const marketCode = getMarketCode();

  const onFormSubmit = (submitData: VehicleFormSubmitData) => {
    const { detailedVehicle, virtualVehicle, formData } = submitData;

    const partialVehicle = {
      odometerReadingValue: formData.odometerReadingValue,
      odometerReadingUnit: formData.odometerReadingUnit,
      purchaseInfoOfType: formData.purchaseType ?? '',
      managedBy: decodedToken?.sub,
      inventoryStatus: 'InStock',
    };

    if (detailedVehicle) {
      const payload: CreateVehicleInputPayload = {
        vehicle: newVehicle({
          detailedVehicle: detailedVehicle!,
          modelYear: marketCode === 'NO' ? formData.modelYear?.year?.toString() : undefined,
          ...partialVehicle,
        }),
        processId: formData.applyProcess,
        vin: detailedVehicle.vin,
        registrationNumber: detailedVehicle.registrationNumber,
      };
      createVehicle(payload);
    } else if (virtualVehicle) {
      const { registrationNumber, vin } = separateRegVin({
        regOrVin: formData.registrationOrVinNumber.registrationNumber,
        marketCode: formData.registrationOrVinNumber.marketCode,
      });
      const payload: CreateVehicleInputPayload = {
        vehicle: newVehicle({
          detailedVehicle: detailedVehicle!,
          oemId: virtualVehicle.id,
          ...partialVehicle,
        }),
        vin,
        registrationNumber,
        processId: formData.applyProcess,
      };
      createVehicle(payload);
    }
  };

  useEffect(() => {
    if (requestCreateVehicle.isSuccess) {
      navigate(
        `/${branch?.id}/vehicles/administrate-vehicles/${createVehicleResponse?.vehicle.waykeId}?new=true`,
      );
      onClose();
    }
  }, [
    requestCreateVehicle.isSuccess,
    navigate,
    branch?.id,
    createVehicleResponse?.vehicle.waykeId,
    onClose,
  ]);

  return (
    <>
      <ShallowPanelBody>
        <CreateRegisteredVehicleForm
          formId="create-registered-vehicle-form"
          onSubmit={onFormSubmit}
          variant="standalone"
          navigatePanel={navigatePanel}
          showTemplateSelect={showTemplateSelect}
          setShowTemplateSelect={setShowTemplateSelect}
          disableVirtual={disableVirtual}
          disabled={requestCreateVehicle.isLoading}
        />
      </ShallowPanelBody>
      <CreateVehicleFooter
        onSubmit={() => {
          const formElement = document.getElementById('create-registered-vehicle-form');
          if (formElement instanceof HTMLFormElement) {
            formElement.requestSubmit();
          }
        }}
        onClose={() => onClose()}
        disabled={false}
        loading={requestCreateVehicle.isLoading}
      />
    </>
  );
};

export default CreateRegisteredVehicle;
